import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Newspaper, ExternalLink, ArrowRight, Loader2, Clock, Globe2 } from "lucide-react";
import { listPublicNews } from "@/lib/api/news.functions";
import { FacebookShareButton } from "@/components/site/FacebookShareButton";
import { DailyLegalCard } from "@/components/site/DailyLegalCard";

const SITE_URL = "https://nkadimengdannylcs.com";

export const Route = createFileRoute("/news")({
  head: () => ({
    meta: [
      { title: "News & Updates — Labour, Immigration & Compliance" },
      {
        name: "description",
        content:
          "Current news and updates from Nkadimeng Danny Legal & Compliance Support — labour law, immigration, compliance and consumer-rights developments in South Africa.",
      },
      { property: "og:title", content: "News & Updates — Nkadimeng Danny Legal" },
      {
        property: "og:description",
        content:
          "Stay up to date on South African labour, immigration and compliance news.",
      },
      { property: "og:url", content: "https://nkadimengdannylcs.com/news" },
    ],
    links: [{ rel: "canonical", href: "https://nkadimengdannylcs.com/news" }],
  }),
  component: NewsPage,
});

const CATEGORY_COLORS: Record<string, string> = {
  legal: "bg-navy/10 text-navy",
  labour: "bg-emerald-100 text-emerald-800",
  immigration: "bg-blue-100 text-blue-800",
  compliance: "bg-amber-100 text-amber-800",
  consumer: "bg-rose-100 text-rose-800",
  education: "bg-purple-100 text-purple-800",
  general: "bg-muted text-foreground",
};

function relativeTime(iso?: string | null) {
  if (!iso) return "";
  const d = new Date(iso).getTime();
  if (!Number.isFinite(d)) return "";
  const diff = Math.max(0, Date.now() - d);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  const w = Math.floor(days / 7);
  if (w < 5) return `${w}w ago`;
  const mo = Math.floor(days / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

function readingTime(text?: string | null) {
  const words = (text ?? "").trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}

function NewsPage() {
  const fetchNews = useServerFn(listPublicNews);
  const { data, isLoading } = useQuery({
    queryKey: ["public-news"],
    queryFn: () => fetchNews(),
  });
  const posts = data?.posts ?? [];

  return (
    <div className="container-narrow py-12 space-y-10">
      <header className="max-w-3xl">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Stay informed</p>
        <h1 className="mt-2 font-display text-4xl text-navy">News &amp; updates</h1>
        <p className="mt-4 text-foreground/80">
          Current developments in South African labour law, immigration,
          compliance and consumer rights — curated to keep our clients ahead of
          the curve.
        </p>
      </header>

      <section aria-labelledby="daily-legal-heading">
        <h2 id="daily-legal-heading" className="sr-only">Latest legal story</h2>
        <DailyLegalCard />
      </section>


      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading latest stories…
        </div>
      ) : posts.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center">
          <Newspaper className="mx-auto h-8 w-8 text-gold" />
          <p className="mt-3 font-display text-lg text-navy">No posts yet</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Check back soon — the admin publishes updates here regularly.
          </p>
        </div>
      ) : (
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {posts.map((p: any) => (
            <article
              key={p.id}
              className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-soft transition hover:-translate-y-0.5 hover:shadow-lifted"
            >
              {p.cover_image_url ? (
                <img
                  src={p.cover_image_url}
                  alt=""
                  className="h-40 w-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="h-40 w-full bg-gradient-to-br from-navy/10 to-gold/10 flex items-center justify-center">
                  <Newspaper className="h-8 w-8 text-navy/30" />
                </div>
              )}
              <div className="flex flex-1 flex-col p-5">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
                  <span
                    className={`rounded-full px-2.5 py-0.5 font-semibold uppercase tracking-wide ${
                      CATEGORY_COLORS[p.category] ?? CATEGORY_COLORS.general
                    }`}
                  >
                    {p.category}
                  </span>
                  {p.source_name && (
                    <span className="inline-flex items-center gap-1 text-muted-foreground">
                      <Globe2 className="h-3 w-3" /> {p.source_name}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1 text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    {relativeTime(p.published_at)}
                  </span>
                </div>
                <h2 className="mt-3 font-display text-base leading-snug text-navy line-clamp-2">
                  {p.title}
                </h2>
                {p.excerpt && (
                  <p className="mt-1.5 flex-1 text-[13px] leading-relaxed text-muted-foreground line-clamp-2">
                    {p.excerpt}
                  </p>
                )}
                <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 border-t border-border/60 pt-3 text-[11px]">
                  <div>
                    <dt className="text-muted-foreground">Published</dt>
                    <dd className="font-medium text-navy">
                      {new Date(p.published_at).toLocaleDateString("en-ZA", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Read time</dt>
                    <dd className="font-medium text-navy">{readingTime(p.excerpt)} min</dd>
                  </div>
                </dl>
                <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
                  <Link
                    to={"/news/$slug" as any}
                    params={{ slug: p.slug } as any}
                    aria-label={`Read full article: ${p.title}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-navy hover:text-gold"
                  >
                    Read full article <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                  {p.source_url && (
                    <a
                      href={p.source_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2.5 py-1 text-[11px] font-medium text-navy hover:border-navy hover:text-gold"
                    >
                      Source <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
                <div className="mt-3 flex justify-end">
                  <FacebookShareButton
                    url={`${SITE_URL}/news/${p.slug}`}
                    caption={`${p.title}${p.excerpt ? `\n\n${p.excerpt}` : ""}`}
                    variant="icon"
                    kind="news"
                    itemId={p.id}
                    itemSlug={p.slug}
                  />
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
